/**
 * Chloe Chat — drop-in launcher for the Humanity Schedule web app.
 *
 * Replaces the Salesforce Embedded Service Chat bubble. Zero dependencies,
 * no build step, no bundler required. Include it and call ChloeChat.init().
 *
 *   <script src="/vendor/chloe-chat.js"></script>
 *   <script>
 *     ChloeChat.init({
 *       host: 'https://chloe-test.tcpsoftware.net',
 *       tokenEndpoint: '/internal/mint-chloe-jwt',
 *     });
 *   </script>
 *
 * What it does:
 *   - Renders a launcher button, bottom-right by default
 *   - Mints a token from YOUR endpoint on open, never on page load
 *   - Loads Chloe in an iframe scoped to the configured host
 *   - Answers the widget's token-refresh request so long conversations survive
 *   - Tears the iframe down on close, so no session state carries over
 *
 * What it deliberately does NOT do:
 *   - Sign anything. The RSA private key stays on your servers
 *   - Store anything in localStorage, sessionStorage, or a cookie
 *   - Read or forward any customer data. Identity travels only in the token
 *
 * Contract reference: backend/CLAIMS.md in this kit
 */
(function (global) {
  'use strict';

  var HOSTS = {
    prod: 'https://chloe.tcpsoftware.net',
    test: 'https://chloe-test.tcpsoftware.net',
  };

  var PRODUCT = 'humanity'; // Humanity Schedule

  var defaults = {
    host: HOSTS.test,
    tokenEndpoint: '/api/v2/chloe/token',
    // Passed straight through as ?lang=. Leave null to let Chloe default to English.
    lang: null,
    // 'launcher' renders the button and opens on click.
    // 'inline' mounts straight into `container` with no button.
    mode: 'launcher',
    container: null,
    label: _s('Support'),
    // Fired on open, close, and mint failure. Wire your own analytics here.
    onEvent: null,
  };

  var config = null;
  var els = { launcher: null, panel: null, frame: null, backdrop: null };
  var listening = false;
  var opening = false;
  var hiddenByHost = false;
  var launcherHidden = false;

  function assign(target, source) {
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
        target[key] = source[key];
      }
    }
    return target;
  }

  function chatOrigin() {
    return new URL(config.host).origin;
  }

  function emit(name, detail) {
    // route the two failure events to Sentry, following the pattern in
    // layout/js/csrfInterceptor.js, so a broken mint endpoint surfaces in telemetry instead
    // of being discovered from customer reports. Guarded like the onEvent callback just
    // below: emit() runs inside open()'s and the refresh listener's catch blocks, right
    // before the user-facing _status() message, so a telemetry failure here (SentryWrapper
    // missing, or exceptionHandling itself throwing) must never suppress that message.
    if (name === 'open_failed' || name === 'token_refresh_failed') {
      try {
        if (typeof SentryWrapper !== 'undefined' && SentryWrapper && typeof SentryWrapper.exceptionHandling === 'function') {
          SentryWrapper.exceptionHandling((detail && detail.message) || name, { event: name });
        }
      } catch (e) { /* never break the host page */ }
    }
    if (typeof config.onEvent === 'function') {
      try { config.onEvent(name, detail || {}); } catch (e) { /* never break the host page */ }
    }
  }

  function mintToken() {
    // The vendor fetch carries no CSRF token. This app's SecuritySubscriber
    // rejects any non-GET/HEAD/OPTIONS request from a signed-in session without one, and
    // layout/js/csrfInterceptor.js only patches XMLHttpRequest.prototype.send, not fetch, so
    // every mint would otherwise be rejected. Read the cookie fresh on every call (never
    // cache it, e.g. at init()) because shouldRegenerateToken() can rotate it mid-session,
    // and the ~55-minute refresh path below calls mintToken() again.
    var headers = { Accept: 'application/json' };
    var csrfToken = StorageService.cookie.getCookie(getCsrfTokenName());
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    var accessToken = sessionStorage.getItem('accessToken');
    if (accessToken) {
      headers.Authorization = 'Bearer ' + accessToken;
    }

    return fetch(config.tokenEndpoint, {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      cache: 'no-store',
    }).then(function (response) {
      if (!response.ok) throw new Error('Chloe token mint failed: HTTP ' + response.status);
      return response.json();
    }).then(function (body) {
      if (body && body.status === 3) {
        var sessionError = new Error('Chloe token mint: session expired or unauthenticated');
        sessionError.chloeSessionExpired = true;
        throw sessionError;
      }

      var jwt = body && body.data ? body.data.jwt : null;
      if (typeof jwt !== 'string' || jwt.length === 0) {
        throw new Error('Chloe token mint returned no jwt');
      }

      return jwt;
    });
  }

  function buildChatUrl(token) {
    var url = new URL('/', config.host);
    url.searchParams.set('jwt', token);
    url.searchParams.set('surface', 'web');
    if (config.lang) url.searchParams.set('lang', config.lang);
    return url.toString();
  }

  /** The widget asks its host for a fresh token five minutes before the
   *  current one expires, and gives the host 30 seconds to answer. Miss it
   *  and the customer's conversation ends mid-sentence. */
  function startRefreshListener() {
    if (listening) return;
    listening = true;

    global.addEventListener('message', function (event) {
      if (event.origin !== chatOrigin()) return;
      if (!event.data || event.data.type !== 'jwt_refresh_request') return;
      if (!els.frame || event.source !== els.frame.contentWindow) return;

      mintToken().then(function (token) {
        event.source.postMessage({ type: 'jwt_refresh', token: token }, chatOrigin());
        emit('token_refreshed', {});
      }).catch(function (error) {
        // Nothing to send. The widget times out after 30s and closes the
        // session with its own message, which is the correct degradation.
        emit('token_refresh_failed', { message: error.message });
        // A session that expires mid-conversation takes this path. Tell the
        // customer to re-authenticate rather than leaving them with only the widget's
        // generic 30s timeout as a signal. Sentry reporting is wired centrally in emit().
        if (error.chloeSessionExpired) {
          _status(_s('Your session has expired. Please re-authenticate.'));
        }
      });
    });
  }

  function injectStyles() {
    if (document.getElementById('chloe-chat-styles')) return;
    var style = document.createElement('style');
    style.id = 'chloe-chat-styles';
    style.textContent = [
      // Same host-page isolation as .chloe-close below. A host that sizes every <button>
      // would otherwise force its own height on the launcher.
      'button.chloe-launcher{position:fixed;right:24px;bottom:24px;z-index:2147483000;',
      'box-sizing:border-box;width:auto;height:auto;',
      'display:inline-flex;align-items:center;gap:8px;padding:12px 20px;border:0;',
      'border-radius:28px;background:#085DD4;color:#fff;font:600 15px/1 system-ui,-apple-system,',
      '"Segoe UI",Roboto,sans-serif;cursor:pointer;box-shadow:0 4px 16px rgba(2,24,46,.28);',
      'text-shadow:none}',
      'button.chloe-launcher:hover{background:#0B4FAE;box-shadow:0 4px 16px rgba(2,24,46,.28)}',
      'button.chloe-launcher:focus-visible{outline:3px solid #085DD4;outline-offset:3px}',
      'button.chloe-launcher[hidden]{display:none}',
      '.chloe-panel{position:fixed;right:24px;bottom:24px;width:400px;height:600px;',
      'max-width:calc(100vw - 32px);max-height:calc(100vh - 32px);z-index:2147483001;',
      'border-radius:12px;overflow:hidden;background:#fff;',
      'box-shadow:0 12px 40px rgba(2,24,46,.32)}',
      '.chloe-panel[hidden]{display:none}',
      '.chloe-panel iframe{display:block;width:100%;height:100%;border:0}',
      // Scoped to .chloe-panel so these outrank a host page that styles every <button>.
      // The Humanity app does: green background, 8px/12px padding and a green button:hover.
      '.chloe-panel .chloe-close{position:absolute;top:8px;right:8px;z-index:2;width:28px;height:28px;',
      'box-sizing:border-box;padding:0;display:flex;align-items:center;justify-content:center;',
      'border:0;border-radius:14px;background:rgba(2,24,46,.55);color:#fff;font:600 18px/1 system-ui;',
      'cursor:pointer;box-shadow:none;text-shadow:none}',
      '.chloe-panel .chloe-close:hover{background:rgba(2,24,46,.75);box-shadow:none}',
      '.chloe-panel .chloe-close:focus-visible{outline:2px solid #fff;outline-offset:1px}',
      '@media (max-width:520px){.chloe-panel{right:0;bottom:0;width:100vw;height:100vh;',
      'max-width:100vw;max-height:100vh;border-radius:0}}',
    ].join('');
    document.head.appendChild(style);
  }

  // The launcher is hidden while the panel is open, while the host page has called hide(),
  // and while the host owns the entry point through hideLauncher(). All three are applied
  // here, so a launcher rendered later still comes up in the state the page last asked for.
  function applyVisibility() {
    if (els.launcher) els.launcher.hidden = hiddenByHost || launcherHidden || els.panel !== null;
    if (els.panel) els.panel.hidden = hiddenByHost;
  }

  function hide() {
    hiddenByHost = true;
    applyVisibility();
  }

  function show() {
    hiddenByHost = false;
    applyVisibility();
  }

  // Hides the launcher and leaves the panel alone. The Gainsight KC Bot owns the entry point
  // on pages where it loads and calls this on every DOM change, so hiding the panel here
  // would make the KC Bot's own confirmChatStart() open an invisible chat.
  function hideLauncher() {
    launcherHidden = true;
    applyVisibility();
  }

  // A cross-origin iframe reports no load status: onload fires for a 503 from the chat host
  // just as it does for the app, and the customer is left with an empty white panel. Resource
  // Timing exposes responseStatus for the frame navigation, so the panel is only torn down
  // when a failing status is actually read. Where responseStatus is not exposed the entry is
  // missing or 0 and nothing happens, which is the behavior before this check.
  function closeOnFailedFrameResponse(url) {
    if (!global.performance || typeof global.performance.getEntriesByName !== 'function') return;

    var entries = global.performance.getEntriesByName(url);
    var entry = entries.length ? entries[entries.length - 1] : null;
    var status = entry ? entry.responseStatus : 0;

    if (!status || status < 400) return;

    emit('open_failed', { message: 'Chloe chat host returned HTTP ' + status });
    close();
    // Not the mint-failure message: a failing status from the chat host is transient, so it
    // asks for a retry instead of sending the customer to email.
    _status(_s('Support chat is temporarily unavailable. Please try again later.'));
  }

  function mountFrame(token, parent) {
    var frame = document.createElement('iframe');
    frame.src = buildChatUrl(token);
    // user-visible string, run through _s() per repo convention.
    frame.title = _s('TCP Support Chat');
    frame.setAttribute('allow', 'clipboard-write');
    frame.addEventListener('load', function () {
      // The handler outlives its own iframe: a panel closed mid-navigation still fires load,
      // and the stale frame's failing status would then close whatever panel is open now.
      if (els.frame !== frame) return;
      closeOnFailedFrameResponse(frame.src);
    });
    parent.appendChild(frame);
    els.frame = frame;
    startRefreshListener();
    return frame;
  }

  function open() {
    if (opening) return Promise.resolve();

    // An explicit open is the customer asking for the chat, so it clears a page-level hide.
    // Without this, a panel built while hidden stays invisible and every later open() returns
    // on the els.panel check below with no request and no error.
    hiddenByHost = false;

    if (els.panel) {
      applyVisibility();
      return Promise.resolve();
    }

    opening = true;

    return mintToken().then(function (token) {
      injectStyles();

      var panel = document.createElement('div');
      panel.className = 'chloe-panel';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', _s('TCP Support Chat'));

      var close = document.createElement('button');
      close.className = 'chloe-close';
      close.type = 'button';
      close.setAttribute('aria-label', _s('Close support chat'));
      close.textContent = '×';
      close.addEventListener('click', function () { ChloeChat.close(); });
      panel.appendChild(close);

      mountFrame(token, panel);
      document.body.appendChild(panel);
      els.panel = panel;

      applyVisibility();
      close.focus();
      emit('opened', {});
    }).catch(function (error) {
      emit('open_failed', { message: error.message });
      // A session-expired mint (the status:3 shape detected in mintToken() above) gets a
      // re-authenticate message; any other mint failure gets a generic unavailable message.
      // Sentry reporting for both event names is wired centrally in emit().
      if (error.chloeSessionExpired) {
        _status(_s('Your session has expired. Please re-authenticate.'));
      } else {
        _status(_s('Support chat is temporarily unavailable. Please email support@tcpsoftware.com.'));
      }
      throw error;
    }).finally(function () {
      opening = false;
    });
  }

  function close() {
    if (els.panel && els.panel.parentNode) els.panel.parentNode.removeChild(els.panel);
    els.panel = null;
    els.frame = null;
    applyVisibility();
    if (els.launcher && !els.launcher.hidden) els.launcher.focus();
    emit('closed', {});
  }

  function renderLauncher() {
    injectStyles();
    // init() is idempotent: a second call replaces the launcher rather than
    // stacking a duplicate on top of it.
    if (els.launcher && els.launcher.parentNode) {
      els.launcher.parentNode.removeChild(els.launcher);
      els.launcher = null;
    }
    var button = document.createElement('button');
    button.className = 'chloe-launcher';
    button.type = 'button';
    button.textContent = config.label;
    button.addEventListener('click', function () { open().catch(function () {}); });
    document.body.appendChild(button);
    els.launcher = button;
    applyVisibility();
  }

  function resolveContainer(target) {
    if (!target) return null;
    return typeof target === 'string' ? document.querySelector(target) : target;
  }

  function initInline() {
    var host = resolveContainer(config.container);
    if (!host) throw new Error('ChloeChat: mode "inline" requires a valid container');
    return mintToken().then(function (token) {
      injectStyles();
      mountFrame(token, host);
      emit('opened', {});
    });
  }

  var ChloeChat = {
    HOSTS: HOSTS,
    PRODUCT: PRODUCT,

    /** Call once, after DOM ready. */
    init: function (options) {
      config = assign(assign({}, defaults), options || {});
      if (!config.host) throw new Error('ChloeChat: host is required');
      // Fail loudly on a malformed host rather than silently loading nothing.
      chatOrigin();

      if (config.mode === 'inline') return initInline();
      renderLauncher();
      return Promise.resolve();
    },

    open: open,
    close: close,

    /** Hide and show the whole widget. The state survives a launcher rendered later. */
    hide: hide,
    show: show,

    /** Hide only the launcher, for a host that provides its own way into the chat. */
    hideLauncher: hideLauncher,
    isOpen: function () { return els.panel !== null; },
  };

  global.ChloeChat = ChloeChat;
})(window);
